/* MAIE merchandising engine v2 (Sep 2026).
   Plain blocking script in <head>: sections call MAIE.merch.run(pool) inline as they are
   parsed, so the first selection happens before paint with no layout shift.

   What it does, in order:
   1. Ranks every candidate a section rendered (server pool) by quality, freshness, season,
      exposure (what this shopper has already seen, decaying over 14 days) and a fresh
      per-visit shuffle — then picks `show` of them under category / colour / style quotas.
   2. Coordinates the whole page: nothing visible repeats; hand-picked tiles are claimed
      first; the first card of each section leads with a different product type where
      possible; hidden tab panels may overlap each other but never the visible page.
   3. After the page has loaded, quietly fetches a random page, in a random order, of the
      compact feed (/collections/<handle>?view=merch) for each source and caches a sample of
      it, so from the next visit on the ranking draws on cross-sections of the whole
      catalogue, not the first 50 products of a collection. Cached candidates are rendered
      with the theme's own card renderer.
   4. Remembers which photo of a product it showed last and shows a different one next
      time, when the product has several strong photos. */
window.MAIE = window.MAIE || {};
(function () {
  var W = window.MAIE;
  if (W.merch) return;

  var K_SEEN = 'maie_seen_cro1';
  var K_VIEW = 'maie_viewed_v1';
  var K_CAT = 'maie_cat_cro1_' + document.documentElement.lang + '_' + (document.documentElement.getAttribute('data-market') || 'US') + '_' + (document.documentElement.getAttribute('data-currency') || 'USD');
  var SORTS = ['title-ascending', 'price-ascending', 'title-descending', 'price-descending', 'created-descending'];
  var HOUR = 3600000, DAY = 86400000;
  var now = Date.now();
  var RESERVES = 2;

  function rd(store, key, fallback) {
    try { var v = store.getItem(key); return v ? JSON.parse(v) : fallback; } catch (e) { return fallback; }
  }
  function wr(store, key, value) {
    try { store.setItem(key, JSON.stringify(value)); } catch (e) {}
  }

  /* ---------- exposure memory: handle -> [times shown, last shown, last photo index] ---------- */
  var seen = rd(localStorage, K_SEEN, {}) || {};
  (function prune() {
    var keys = Object.keys(seen).filter(function (k) { return seen[k] && now - seen[k][1] < 14 * DAY; });
    keys.sort(function (a, b) { return seen[b][1] - seen[a][1]; });
    var out = {};
    for (var i = 0; i < keys.length && i < 500; i++) out[keys[i]] = seen[keys[i]];
    seen = out;
  })();
  function exposure(h) {
    var e = seen[h];
    if (!e) return 0;
    var age = now - e[1];
    var d = age < HOUR ? 1 : age < 6 * HOUR ? 0.85 : age < DAY ? 0.65 : age < 3 * DAY ? 0.4 : age < 7 * DAY ? 0.2 : 0.08;
    return Math.min(40, 20 * d + Math.min(e[0], 4) * 5);
  }
  var seenDirty = false;
  function noteShown(h, imgIndex) {
    var e = seen[h] || [0, 0, 0];
    seen[h] = [e[0] + 1, now, imgIndex == null ? e[2] : imgIndex];
    seenDirty = true;
  }
  function flushSeen() { if (seenDirty) { wr(localStorage, K_SEEN, seen); seenDirty = false; } }

  /* ---------- season (UK / US / CA calendar) ---------- */
  var month = new Date().getMonth();
  if (/^(AU|NZ|ZA|AR|CL|BR)$/.test(document.documentElement.getAttribute('data-market') || '')) month = (month + 6) % 12;
  var SEASON = (month >= 8 && month <= 10) ? 'autumn' : (month === 11 || month <= 1) ? 'winter' : (month >= 2 && month <= 4) ? 'spring' : 'summer';
  var TYPE_WARM = /coat|jacket|trench|cardigan|knit|sweater|blazer|boot|pants|trouser|hood|puffer|fur|fleece|scarf|loafer/i;
  var TYPE_COLD = /swim|bikini|sandal|slide|flip|sunglass|short|hat|cap/i;
  var TAG_WARM = /knit|faux-fur|suede|leather|long-sleeve|plaid|tweed|wool/;
  var TAG_COOL = /strapless|sleeveless|backless|cutout|mesh|halter/;
  function seasonScore(ty, tags) {
    var s = 0;
    if (SEASON === 'autumn' || SEASON === 'winter') {
      if (TYPE_WARM.test(ty)) s += SEASON === 'winter' ? 40 : 30;
      if (TYPE_COLD.test(ty)) s -= 120;
      if (/tank|cami/i.test(ty)) s -= 25;
      if (/lounge|sleep|robe|pyjama|pajama/i.test(ty)) s -= 40;
      if (TAG_WARM.test(tags)) s += 12;
      if (TAG_COOL.test(tags)) s -= SEASON === 'winter' ? 14 : 6;
    } else if (SEASON === 'spring') {
      if (/faux-fur|puffer|wool/.test(tags + ' ' + ty.toLowerCase())) s -= 25;
      if (/trench|blazer|cardigan|jacket/i.test(ty)) s += 15;
    } else {
      if (TYPE_COLD.test(ty)) s += 25;
      if (/coat|puffer|fur|knit|sweater|boot/i.test(ty)) s -= 50;
      if (TAG_COOL.test(tags)) s += 8;
    }
    return s;
  }
  /* Product types roll up into merchandising groups so a quota of 3 "outerwear" really
     means three coats/jackets/blazers in total, not three of each type. */
  var GROUPS = [
    [/swim|bikini/, 'swim'], [/lounge|sleep|robe|pyjama|pajama/, 'lounge'],
    [/dress|gown/, 'dresses'], [/jumpsuit|romper|set\b|sets/, 'sets'],
    [/coat|jacket|blazer|trench|puffer|parka|vest|gilet/, 'outerwear'],
    [/knit|sweater|cardigan|hood|jumper|pullover/, 'knitwear'],
    [/top|blouse|shirt|tee|cami|bodysuit|corset|tunic/, 'tops'],
    [/pant|trouser|jean|skirt|short|legging/, 'bottoms'],
    [/bag|tote|clutch|purse|backpack/, 'bags'],
    [/shoe|flat|sandal|heel|boot|loafer|mule|sneaker|slipper/, 'shoes'],
    [/sunglass|hat|cap|scarf|belt|jewel|accessor|glove|earring|necklace/, 'accessories']
  ];
  function groupOf(ty) {
    ty = (ty || '').toLowerCase();
    for (var i = 0; i < GROUPS.length; i++) if (GROUPS[i][0].test(ty)) return GROUPS[i][1];
    return ty || 'other';
  }
  var STYLES = ['lace', 'satin', 'ruffled', 'polka-dot', 'striped', 'floral', 'leopard', 'sequins', 'suede', 'faux-fur', 'knit', 'leather', 'plaid', 'mesh', 'tailored', 'denim', 'tweed'];
  function styleOf(tags) {
    for (var i = 0; i < STYLES.length; i++) if (tags.indexOf(STYLES[i]) > -1) return STYLES[i];
    return '';
  }

  /* ---------- per-visit shuffle: a new seed on every page load ---------- */
  var session = rd(sessionStorage, 'maie_merch_session_cro1', null);
  if (!session || now - session.t > 4 * HOUR) {session = {seed:(Math.random()*1e9)|0,t:now};wr(sessionStorage,'maie_merch_session_cro1',session);}
  var sess = session.seed;
  function hash32(str, salt) {
    var h = (2166136261 ^ salt) >>> 0;
    for (var i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = (h * 16777619) >>> 0; }
    return h >>> 0;
  }
  function jitter(handle, amount) { return (hash32(handle, sess) % 1000) / 1000 * amount; }

  /* ---------- page-level coordination ---------- */
  var usedOnPage = Object.create(null);
  var leadTypes = Object.create(null);
  var pools = [];
  var catalog = rd(localStorage, K_CAT, {}) || {};

  function candFromEl(el, i) {
    var tags = el.getAttribute('data-tags') || '';
    return {
      el: el, item: null, h: el.getAttribute('data-h') || ('idx-' + i), order: i,
      ty: (el.getAttribute('data-cat') || '').toLowerCase(), col: (el.getAttribute('data-color') || '').toLowerCase(),
      sty: el.getAttribute('data-style') || styleOf(tags), tags: tags,
      base: (parseFloat(el.getAttribute('data-score')) || 0) - (el.hasAttribute('data-flat') ? 80 : 0),
      imgs: parseInt(el.getAttribute('data-imgs'), 10) || 1
    };
  }
  function candFromItem(it, i) {
    var tags = it.tg || '';
    var base = 0;
    if (it.a) base += 100;
    base += Math.min(it.n || 1, 6) * 8;
    if ((it.ar || 1) < 0.9) base += 40;
    if ((it.w || 0) >= 1000) base += 10;
    if (tags.indexOf('new-in') > -1) base += 10;
    /* A product-only photo on white sells less on a homepage than the garment worn. */
    if (it.fl) base -= 80;
    return {
      el: null, item: it, h: it.h, order: 1000 + i,
      ty: (it.ty || '').toLowerCase(), col: ((it.co || '').split('|')[0] || '').toLowerCase(),
      sty: styleOf(tags), tags: tags, base: base, imgs: (it.im || []).length
    };
  }
  function scoreOf(c, jitterAmt) {
    var s = c.base + jitter(c.h, jitterAmt) - exposure(c.h) + Math.max(-20, Math.min(20, seasonScore(c.ty, c.tags)));
    if (c.tags.indexOf('new-in') > -1) s += 15;
    if (c.tags.indexOf('trending') > -1) s += 15;
    return s;
  }

  function settings(pool) {
    var jitterAmt = parseInt(pool.getAttribute('data-mm-jitter'), 10);
    return {
      show: parseInt(pool.getAttribute('data-mm-show'), 10) || 8,
      maxCat: parseInt(pool.getAttribute('data-mm-maxcat'), 10) || 0,
      maxColor: parseInt(pool.getAttribute('data-mm-maxcolor'), 10) || 0,
      maxStyle: parseInt(pool.getAttribute('data-mm-maxstyle'), 10) || 3,
      jitter: isNaN(jitterAmt) ? 40 : Math.min(60, jitterAmt),
      lead: pool.hasAttribute('data-mm-lead'),
      sources: (pool.getAttribute('data-mm-src') || '').split(',').filter(Boolean)
    };
  }

  /* Pick `show` candidates under quotas, in tiers:
       1. fresh (not seen recently) · strict group / colour / style quotas
       2. fresh · relaxed group quota (a "Going out" pool that is 90% dresses may show more
          dresses, but always keeps at least two slots for other groups)
       3. anything · strict quotas      4. anything · relaxed      5. anything at all
     Quotas only apply where the pool is genuinely mixed. */
  function pick(cands, cfg, hidden) {
    var chosen=[],taken={},byGrp={},byCol={},bySty={};
    var groups={};cands.forEach(function(c){c.grp=groupOf(c.ty);groups[c.grp]=1;});
    var useCat=cfg.maxCat && Object.keys(groups).length>=3;
    var saved=/\/pages\/wishlist/.test(location.pathname)?rd(localStorage,'maie_saved_v1',[]):[];
    function take(c,relax){
      if(taken[c.h]||usedOnPage[c.h]||saved.indexOf(c.h)>-1||c.el&&c.el.hasAttribute('data-soldout'))return false;
      if(!relax && ((useCat&&(byGrp[c.grp]||0)>=cfg.maxCat)||(cfg.maxColor&&c.col&&(byCol[c.col]||0)>=cfg.maxColor)||(cfg.maxStyle&&c.sty&&(bySty[c.sty]||0)>=cfg.maxStyle)))return false;
      taken[c.h]=1;byGrp[c.grp]=(byGrp[c.grp]||0)+1;byCol[c.col]=(byCol[c.col]||0)+1;bySty[c.sty]=(bySty[c.sty]||0)+1;chosen.push(c);return true;
    }
    var stable=cands.slice().sort(function(a,b){return b.base-a.base||a.order-b.order;});
    var winners=Math.max(1,Math.floor(cfg.show*.6));
    for(var i=0;i<stable.length&&chosen.length<winners;i++)take(stable[i],false);
    for(var j=0;j<stable.length&&chosen.length<winners;j++)take(stable[j],true);
    var fresh=cands.filter(function(c){return c.tags.indexOf('new-in')>-1;}).sort(function(a,b){return scoreOf(b,15)-scoreOf(a,15)||a.order-b.order;});
    var freshTarget=Math.min(cfg.show,chosen.length+Math.max(1,Math.round(cfg.show*.15)));
    for(var k=0;k<fresh.length&&chosen.length<freshTarget;k++)take(fresh[k],false);
    var explore=cands.slice().sort(function(a,b){return scoreOf(b,cfg.jitter)-scoreOf(a,cfg.jitter)||a.order-b.order;});
    for(var n=0;n<explore.length&&chosen.length<cfg.show;n++)take(explore[n],false);
    for(var m=0;m<explore.length&&chosen.length<cfg.show;m++)take(explore[m],true);
    if(!hidden){chosen.forEach(function(c){usedOnPage[c.h]=1;});if(chosen[0])leadTypes[chosen[0].grp]=1;}
    return chosen;
  }

  /* Photo rotation on a server card: show a different first photo than last time. */
  function rotatePhoto(c) { return 0; }

  function run(pool) {
    if (!pool || pool.getAttribute('data-mm-done')) return;
    pool.setAttribute('data-mm-done', '1');
    var items = [].slice.call(pool.querySelectorAll('[data-mm]'));
    if (!items.length) return;
    var cfg = settings(pool);
    var hidden = !!(pool.closest && pool.closest('[hidden]'));
    var cands = items.map(candFromEl);
    var chosen = pick(cands, cfg, hidden);
    if (!chosen.length) return;

    chosen.forEach(function (c, n) {
      c.el.style.order = n;
      c.el.classList.remove('mm-off');
      c.photo = rotatePhoto(c);
    });
    var taken = {}; chosen.forEach(function (c) { taken[c.h] = 1; });
    var reserves = [];
    cands.forEach(function (c) {
      if (taken[c.h]) return;
      if (!hidden && reserves.length < RESERVES && !usedOnPage[c.h]) { reserves.push(c); return; }
      if (c.el.parentNode) c.el.parentNode.removeChild(c.el);
    });
    var rec = { el: pool, cfg: cfg, chosen: chosen, reserves: reserves, hidden: hidden, exposed: false };
    pools.push(rec);
    if (!hidden) expose(rec);
    pool.classList.add('mm-ready');
  }

  var viewTimers = new WeakMap();
  var impressionObserver = window.IntersectionObserver ? new IntersectionObserver(function(entries){
    entries.forEach(function(entry){
      var el=entry.target;clearTimeout(viewTimers.get(el));
      if(entry.intersectionRatio>=.5&&!document.hidden){
        viewTimers.set(el,setTimeout(function(){
          if(!el.isConnected||!el.getClientRects().length||document.hidden)return;
          var h=el.getAttribute('data-h');if(!h)return;
          var key='maie_imp_cro1_'+sess+'_'+h;
          if(!rd(sessionStorage,key,false)){noteShown(h,0);flushSeen();wr(sessionStorage,key,true);document.dispatchEvent(new CustomEvent('maie:merch-impression',{detail:{handle:h,version:'cro1',surface:el.closest('[data-mm-pool]')?.id||'',position:Number(el.style.order)||0}}));}
          impressionObserver.unobserve(el);
        },1000));
      }
    });
  },{threshold:[0,.5,1]}) : null;
  function expose(rec) {
    rec.exposed=true;
    if(impressionObserver)rec.chosen.forEach(function(c){if(c.el)impressionObserver.observe(c.el);});
  }
  function exposePanel(node) {
    pools.forEach(function (rec) { if (node === rec.el || node.contains(rec.el)) expose(rec); });
  }

  /* A hand-picked product (category / occasion tile) claims its handle: any visible pool
     showing it swaps in its best unused reserve at the same position. */
  function claim(handle) {
    if (!handle) return;
    usedOnPage[handle] = 1;
    pools.forEach(function (rec) {
      for (var i = 0; i < rec.chosen.length; i++) {
        var c = rec.chosen[i];
        if (c.h !== handle) continue;
        var sub = null;
        while (rec.reserves.length && !sub) { var r = rec.reserves.shift(); if (!usedOnPage[r.h]) sub = r; }
        if (sub && sub.el) {
          sub.el.style.order = c.el.style.order; sub.el.classList.remove('mm-off');
          usedOnPage[sub.h] = 1; rec.chosen[i] = sub; sub.photo = rotatePhoto(sub);
          if (rec.exposed && impressionObserver) impressionObserver.observe(sub.el);
        } else { rec.chosen.splice(i, 1); }
        if (c.el && c.el.parentNode) c.el.parentNode.removeChild(c.el);
        break;
      }
    });
    flushSeen();
  }

  /* ---------- the whole catalogue: cached compact feeds ---------- */
  function cachedItems(sources) {
    var out = [], have = {};
    sources.forEach(function (s) {
      var e = catalog[s];
      if (!e || now - e.t > DAY || !e.items || s === '__cdn') return;
      e.items.forEach(function (it) { if (!have[it.h]) { have[it.h] = 1; out.push(it); } });
    });
    return out;
  }
  function itemToProduct(it, photo) {
    var sizes = it.sz ? it.sz.split('|') : [], cols = it.co ? it.co.split('|') : [];
    var options = [], cp = it.cp;
    if (cp === 0) { options.push({ name: 'Color', values: cols }); if (sizes.length) options.push({ name: 'Size', values: sizes }); }
    else { if (sizes.length) options.push({ name: 'Size', values: sizes }); if (cols.length) options.push({ name: 'Color', values: cols }); }
    var cpos = -1; options.forEach(function (o, i) { if (o.name === 'Color') cpos = i; });
    var variants = [{ id: it.vid, options: [], available: !!it.a, featured_image: null }];
    (it.vi || []).forEach(function (pair) {
      if (!pair) return;
      var opts = []; if (cpos > -1) opts[cpos] = pair[0];
      variants.push({ id: it.vid, options: opts, featured_image: { src: expand(pair[1]) } });
    });
    var imgs = (it.im || []).map(expand);
    var first = photo || 0;
    if (first > 0 && imgs[first]) { var f = imgs.splice(first, 1)[0]; imgs.unshift(f); }
    return {
      id: it.id, product_type: it.ty, handle: it.h, title: it.t, url: it.u || (((W.routes && W.routes.root) || '/').replace(/\/$/, '') + '/products/' + it.h), price: it.p, compare_at_price: it.c || 0, available: !!it.a,
      images: imgs, tags: (it.tg || '').split('|'), options: options, variants: variants,
      rating: it.rn > 0 ? { r: it.r, n: it.rn } : null, type: it.ty
    };
  }

  /* Second pass at DOMContentLoaded: rank cached catalogue candidates against the current
     picks and replace weaker cards in place (same size, so nothing shifts). Only visible pools. */
  function upgrade() {
    if (!W.renderTiles) return;
    var tmp = document.createElement('div');
    pools.forEach(function (rec) {
      if (rec.hidden || !rec.cfg.sources.length || rec.el.getBoundingClientRect().top < innerHeight + 200) return;
      var items = cachedItems(rec.cfg.sources);
      if (items.length < 8) return;
      var cands = rec.chosen.slice();
      var have = {}; cands.forEach(function (c) { have[c.h] = 1; });
      items.forEach(function (it, i) { if (!have[it.h] && !usedOnPage[it.h] && it.a) cands.push(candFromItem(it, i)); });
      /* current picks keep their claim; release it for the re-rank and re-claim after */
      rec.chosen.forEach(function (c) { delete usedOnPage[c.h]; });
      var cfg = rec.cfg;
      var stable=rec.chosen.slice(0,Math.max(1,Math.floor(cfg.show*.6)));
      stable.forEach(function(c){usedOnPage[c.h]=1;});
      var extraCfg=Object.assign({},cfg,{show:cfg.show-stable.length});
      var next=stable.concat(pick(cands,extraCfg,false));
      var keep = {}; next.forEach(function (c) { keep[c.h] = 1; });
      var slots = rec.chosen.map(function (c) { return c.el; });
      var newChosen = [];
      next.forEach(function (c, n) {
        if (c.el) { c.el.style.order = n; newChosen.push(c); return; }
        var photo = 0;
        tmp.innerHTML = W.renderTiles([itemToProduct(c.item, photo)]);
        var el = tmp.firstElementChild;
        if (!el) return;
        el.style.order = n; el.setAttribute('data-h', c.h); el.setAttribute('data-mm', ''); el.setAttribute('data-cat', c.ty); el.setAttribute('data-color', c.col);
        rec.el.appendChild(el);
        c.el = el; c.photo = photo; newChosen.push(c);
        if(impressionObserver)impressionObserver.observe(el);
      });
      slots.forEach(function (el) { var h = el.getAttribute('data-h'); if (!keep[h] && el.parentNode) el.parentNode.removeChild(el); });
      rec.chosen = newChosen;
      if (rec.el.scrollLeft) rec.el.scrollLeft = 0;
    });
    flushSeen();
    if (W.watchCards) W.watchCards(document);
    if (W.paintHearts) W.paintHearts();
  }

  /* Cached items keep only the part of each image URL after the store's CDN prefix. */
  var CDN = '';
  function compact(it) {
    delete it.u;
    var ims = (it.im || []).slice(0, 3);
    if (!CDN && ims[0]) { var m = ims[0].match(/^(https?:)?\/\/[^/]+\/[^?]*\/files\//); if (m) CDN = m[0]; }
    if (CDN) {
      it.im = ims.map(function (u) { return u.indexOf(CDN) === 0 ? u.slice(CDN.length) : u; });
      it.vi = (it.vi || []).filter(Boolean).map(function (pair) { return [pair[0], pair[1].indexOf(CDN) === 0 ? pair[1].slice(CDN.length) : pair[1]]; });
    } else { it.im = ims; it.vi = (it.vi || []).filter(Boolean); }
  }
  function expand(u) { return /^(https?:)?\/\//.test(u) ? u : (catalogBase() + u); }
  function catalogBase() { return CDN || (catalog.__cdn || ''); }

  /* Lazy tab panel: candidates arrive as compact JSON inside the panel; cards are built the
     moment the tab opens, ranked together with the cached catalogue. */
  function fill(panel) {
    var pool = panel.querySelector('[data-mm-lazy]');
    if (!pool || pool.getAttribute('data-mm-filled') || !W.renderTiles) return;
    var script = panel.querySelector('script[data-mm-items]'), items = [];
    try { items = JSON.parse(script ? script.textContent : '[]') || []; } catch (e) { items = []; }
    var base = script ? (script.getAttribute('data-cdn') || '') : '';
    if (base) items.forEach(function (it) {
      it.im = (it.im || []).map(function (u) { return /^(https?:)?\/\//.test(u) ? u : base + u; });
      it.vi = (it.vi || []).filter(Boolean).map(function (pair) { return [pair[0], /^(https?:)?\/\//.test(pair[1]) ? pair[1] : base + pair[1]]; });
    });
    pool.setAttribute('data-mm-filled', '1');
    var cfg = settings(pool);
    var cands = items.filter(function (it) { return it && it.a; }).map(candFromItem);
    var have = {}; cands.forEach(function (c) { have[c.h] = 1; });
    cachedItems(cfg.sources).forEach(function (it, i) { if (!have[it.h] && it.a) cands.push(candFromItem(it, 500 + i)); });
    /* Only one tab is visible at a time, so a lazy tab avoids what the page shows by default
       but does not claim its own picks: a dress may sit in both "Dresses" and "Sale". */
    var chosen = pick(cands, cfg, true);
    var html = '';
    chosen.forEach(function (c) {
      var e = seen[c.h];
      c.photo = 0;
      html += W.renderTiles([itemToProduct(c.item, c.photo)]);
    });
    pool.innerHTML = html;
    [].forEach.call(pool.children, function (el, n) {
      if (!chosen[n]) return;
      el.style.order = n; el.setAttribute('data-h', chosen[n].h); el.setAttribute('data-mm', ''); el.setAttribute('data-cat', chosen[n].ty); el.setAttribute('data-color', chosen[n].col);
      chosen[n].el = el;
    });
    pools.push({ el: pool, cfg: cfg, chosen: chosen, reserves: [], hidden: false, exposed: false });
    if (W.watchCards) W.watchCards(panel);
    if (W.paintHearts) W.paintHearts();
    pool.classList.add('mm-ready');
  }

  /* Background refresh of the cached feeds: one random page per source, at most once a day,
     never on save-data, and only where the page actually has pools. */
  function refresh() {
    var c = navigator.connection || {};
    if (c.saveData || !window.fetch) return;
    var todo = {}, counts = {};
    [].forEach.call(document.querySelectorAll('[data-mm-pool][data-mm-src]'), function (el) {
      var srcs = (el.getAttribute('data-mm-src') || '').split(',').filter(Boolean);
      var visible = !(el.closest && el.closest('[hidden]'));
      srcs.forEach(function (s) { todo[s] = Math.max(todo[s] || 0, visible ? 2 : 1); });
      /* product counts let the very first fetch land on a random page, not always page 1 */
      var cnt = (el.getAttribute('data-mm-count') || '').split(',').filter(Boolean);
      cnt.forEach(function (c, i) {
        var pair = c.split(':'), h = pair.length > 1 ? pair[0] : srcs[i], n = parseInt(pair[pair.length - 1], 10);
        if (h && n > 0) counts[h] = Math.ceil(n / 50);
      });
    });
    /* A source is refreshed when its cache is a day old, or while it is still shallow: until
       ~120 products (or every page of a small collection) are cached, each visit adds pages. */
    var keys = Object.keys(todo).filter(function (s) {
      var e = catalog[s]; if (!e) return true;
      if (now - (e.t || 0) > 20 * HOUR) return true;
      var pagesSeen = (e.seen || []).length, pages = e.pages || 1;
      return (e.items || []).length < 120 && pagesSeen < pages * SORTS.length && now - (e.t || 0) > 30000;
    });
    if (!keys.length) return;
    var root = (W.routes && W.routes.root) || '/';
    if (root.charAt(root.length - 1) === '/') root = root.slice(0, -1);
    /* Sources the page is showing right now come first, then the shallowest caches, so a
       short visit still deepens what the next visit will draw on. Breadth first: one page for
       every source, then a second page for the large ones. */
    var depth = function (s) { var e = catalog[s]; return e && e.items ? e.items.length : 0; };
    keys.sort(function (a, b) { return (todo[b] - todo[a]) || (depth(a) - depth(b)); });
    var queue = keys.slice();
    keys.forEach(function (s) { var e = catalog[s]; if (((e && e.pages) || counts[s] || 1) > 2) queue.push(s); });
    var i = 0, active = 0;
    function nextOne() {
      if (i >= queue.length) { if (!active && CDN) { catalog.__cdn = CDN; wr(localStorage, K_CAT, catalog); } return; }
      active++;
      var s = queue[i++]; var e = catalog[s] || {};
      var pages = e.pages || counts[s] || 1, seenPages = e.seen || [];
      /* The store's "best selling" order has almost no sales behind it and runs category by
         category (fifty coats, then fifty dresses), which would starve the second visit of
         variety. So each fetch reads a random page in a random order (by title, by price, by
         date): every such page is a different cross-section of the collection, and only a
         random 30 of its 50 products are kept, so the cache holds four or more cross-sections
         rather than two blocks. Ranking is done here, never by the sort. */
      var sort = SORTS[Math.floor(Math.random() * SORTS.length)];
      var page = 1 + Math.floor(Math.random() * pages), key = sort + ':' + page;
      /* prefer a page this browser has not cached yet */
      for (var tries = 0; tries < 6 && seenPages.indexOf(key) > -1 && seenPages.length < pages * SORTS.length; tries++) { page = 1 + Math.floor(Math.random() * pages); key = sort + ':' + page; }
      /* No JSON Accept header on purpose: Shopify would answer with its own collection JSON
         instead of rendering the compact view. */
      fetch(root + '/collections/' + s + '?view=merch&sort_by=' + sort + '&page=' + page, { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.text() : null; })
        .then(function (t) { if (!t || t.charAt(0) !== '{') return null; try { return JSON.parse(t); } catch (e) { return null; } })
        .then(function (j) {
          if (j && j.items) {
            var old = (e.items || []), have = {};
            var fresh = j.items.filter(function (it) { return it && it.h && it.a; });
            for (var k = fresh.length - 1; k > 0; k--) { var r = Math.floor(Math.random() * (k + 1)); var tmp = fresh[k]; fresh[k] = fresh[r]; fresh[r] = tmp; }
            fresh = fresh.slice(0, 30);
            fresh.forEach(function (it) { compact(it); have[it.h] = 1; });
            var merged = fresh.concat(old.filter(function (it) { return !have[it.h]; })).slice(0, 120);
            var sp = (e.seen || []).slice(); if (sp.indexOf(key) < 0) sp.push(key);
            if (now - (e.t || 0) > 20 * HOUR) sp = [key];
            catalog[s] = { t: Date.now(), pages: j.pages || 1, last: key, seen: sp, items: merged };
            e = catalog[s];
            if (CDN) catalog.__cdn = CDN;
            wr(localStorage, K_CAT, catalog);
          }
        }).catch(function () {}).then(function () { active--; setTimeout(nextOne, 120); });
    }
    nextOne(); nextOne();
  }

  function runAll(root) {
    var list = (root || document).querySelectorAll('[data-mm-pool]');
    for (var i = 0; i < list.length; i++) { try { run(list[i]); } catch (e) {} }
  }

  /* ---------- recently viewed ---------- */
  function recordView(data) {
    if (!data || !data.h) return;
    var list = rd(localStorage, K_VIEW, []) || [];
    list = list.filter(function (x) { return x && x.h !== data.h; });
    list.unshift(data);
    wr(localStorage, K_VIEW, list.slice(0, 16));
  }
  function recentlyViewed(excludeHandle) {
    var list = rd(localStorage, K_VIEW, []) || [];
    return list.filter(function (x) { return x && x.h && x.h !== excludeHandle; });
  }

  W.merch = {
    run: run, runAll: runAll, claim: claim, expose: exposePanel, upgrade: upgrade, refresh: refresh, fill: fill,
    recordView: recordView, recentlyViewed: recentlyViewed, used: usedOnPage, season: SEASON,
    pools: pools, itemToProduct: itemToProduct
  };

  function onReady() {
    runAll();
    try { upgrade(); } catch (e) {}
    var kick = function () { setTimeout(refresh, 1500); };
    if (document.readyState === 'complete') kick(); else window.addEventListener('load', kick);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', onReady); else onReady();
  window.addEventListener('shopify:section:load', function (e) { runAll(e.target); });

  /* ================= showcase card behaviour ================= */

  function hydrate(card) {
    if (card.getAttribute('data-mm-hydrated')) return;
    card.setAttribute('data-mm-hydrated', '1');
    var imgs = card.querySelectorAll('img[data-mm-src]');
    for (var i = 0; i < imgs.length; i++) {
      imgs[i].src = imgs[i].getAttribute('data-mm-src');
      imgs[i].removeAttribute('data-mm-src');
    }
  }

  function step(card, delta) {
    var slides = card.querySelector('[data-mm-slides]');
    if (!slides) return;
    hydrate(card);
    var count = parseInt(slides.getAttribute('data-count'), 10) || 1;
    var at = parseInt(slides.getAttribute('data-at'), 10) || 0;
    at = ((at + delta) % count + count) % count;
    slides.setAttribute('data-at', at);
    slides.style.transform = 'translateX(' + (-at * 100) + '%)';
  }

  /* Size chips are painted from Liquid so they appear instantly; the product JSON is only
     needed to map a size onto a variant id, and it is fetched once per product. */
  var productCache = Object.create(null);
  function loadProduct(handle) {
    if (productCache[handle]) return productCache[handle];
    var root = (W.routes && W.routes.root) || '/';
    if (root.charAt(root.length - 1) === '/') root = root.slice(0, -1);
    productCache[handle] = fetch(root + '/products/' + handle + '.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) {
        if (!r.ok) throw new Error('product ' + r.status);
        return r.json();
      })
      .catch(function (err) {
        /* Never cache a rejection: a single blip would leave the panel dead for the rest of
           the session because every later open would reuse the failed promise. */
        delete productCache[handle];
        throw err;
      });
    return productCache[handle];
  }

  function fillSizes(card) {
    var panel = card.querySelector('[data-mm-sizes]');
    if (!panel || panel.getAttribute('data-mm-filled')) return Promise.resolve();
    panel.setAttribute('data-mm-filled', '1');
    panel.classList.add('is-loading');

    return loadProduct(card.getAttribute('data-handle')).then(function (p) {
      var sizeIdx = -1;
      (p.options || []).forEach(function (o, i) {
        var n = (o && o.name ? o.name : o).toString().toLowerCase();
        if (n === 'size' || n === 'shoe size') sizeIdx = i;
      });
      panel.querySelectorAll('[data-mm-size]').forEach(function (chip) {
        var want = chip.getAttribute('data-mm-size');
        var match = p.variants.filter(function (v) {
          return sizeIdx > -1 ? v.options[sizeIdx] === want : v.title === want;
        });
        /* The card only offers sizes when size is the product's single remaining choice, so
           this match is unambiguous — there is no colour left to pick on the shopper's
           behalf. */
        var first = match.filter(function (v) { return v.available; })[0];
        if (first) {
          chip.setAttribute('data-vid', first.id);
          chip.disabled = false;
        } else {
          chip.disabled = true;
        }
      });
      panel.classList.remove('is-loading');
    }).catch(function () {
      /* Sizes are unavailable: hand the card back to the product page rather than leaving a
         button that opens an empty panel. */
      panel.removeAttribute('data-mm-filled');
      panel.classList.remove('is-loading');
      panel.hidden = true;
      var link = card.querySelector('[data-mm-atc]');
      if (link) {
        link.removeAttribute('data-mm-atc');
        link.removeAttribute('aria-expanded');
      }
      if (W.toast) W.toast('Sizes could not load. Open the product to choose one.');
    });
  }

  function openSizes(card) {
    var panel = card.querySelector('[data-mm-sizes]');
    var atc = card.querySelector('[data-mm-atc]');
    if (!panel) return;
    var opening = panel.hidden;
    panel.hidden = !opening;
    if (atc) atc.setAttribute('aria-expanded', opening ? 'true' : 'false');
    if (opening) fillSizes(card);
  }

  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;

    var prev = t.closest('[data-mm-prev]');
    var next = prev ? null : t.closest('[data-mm-next]');
    if (prev || next) {
      var navCard = (prev || next).closest('[data-mm-card]');
      if (navCard) { e.preventDefault(); e.stopPropagation(); step(navCard, prev ? -1 : 1); }
      return;
    }

    var atc = t.closest('[data-mm-atc]');
    if (atc) {
      /* Leave modifier and middle clicks alone so "open product in a new tab" still works. */
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || (e.button && e.button !== 0)) return;
      var sizeCard = atc.closest('[data-mm-card]');
      if (sizeCard && sizeCard.querySelector('[data-mm-sizes]')) {
        e.preventDefault();
        openSizes(sizeCard);
      }
      /* No size panel means this is a real link to the product page — let it through. */
      return;
    }

    var chip = t.closest('[data-mm-size]');
    if (chip) {
      e.preventDefault();
      e.stopPropagation();
      var vid = chip.getAttribute('data-vid');
      if (chip.disabled || !vid || !W.addToCart) return;
      chip.classList.add('is-busy');
      W.addToCart(parseInt(vid, 10), 1).then(function () {
        chip.classList.remove('is-busy');
        var c = chip.closest('[data-mm-card]');
        var panel = c && c.querySelector('[data-mm-sizes]');
        if (panel) panel.hidden = true;
        var link = c && c.querySelector('[data-mm-atc]');
        if (link) link.setAttribute('aria-expanded', 'false');
      }).catch(function (err) {
        chip.classList.remove('is-busy');
        if (W.toast) W.toast((err && err.description) ? err.description : 'That size could not be added. Please open the product page.');
      });
    }
  });

  /* Second and third photos load on first intent, never on page load. The variant map is
     warmed only when the shopper reaches the button itself, so hovering a rail of twelve
     cards does not fire twelve requests. */
  function warm(e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var card = t.closest('[data-mm-card]');
    if (card) hydrate(card);
    var atc = t.closest('[data-mm-atc]');
    if (atc) {
      var c = atc.closest('[data-mm-card]');
      if (c && c.querySelector('[data-mm-sizes]')) fillSizes(c);
    }
  }
  document.addEventListener('mouseover', warm, { passive: true, capture: true });
  document.addEventListener('touchstart', warm, { passive: true, capture: true });

})();

(function(){
  var W=window.MAIE=window.MAIE||{},feeds={},claimed={};
  function root(){return (W.routes&&W.routes.root||'/').replace(/\/$/,'');}
  function group(p){
    var ty=String(p.product_type||p.type||p.ty||'').toLowerCase(),title=String(p.title||p.t||'').toLowerCase();
    var rules=[[/bag|tote|clutch|purse|backpack/,'bags'],[/shoe|flat|sandal|heel|boot|loafer|mule|sneaker|slipper/,'shoes'],[/sunglass|jewel|earring|necklace|scarf|hat|belt|accessor/,'accessories'],[/dress|gown/,'dresses'],[/jumpsuit|romper|set\b|sets/,'sets'],[/coat|jacket|blazer|trench|puffer|vest/,'outerwear'],[/knit|sweater|cardigan|hood|jumper/,'knitwear'],[/pant|trouser|jean|skirt|short|legging/,'bottoms'],[/top|blouse|shirt|tee|cami|bodysuit|corset/,'tops']];
    if(/two[ -]piece|matching set|shorts suit/.test(title))return 'sets';
    if(/\bset\b/.test(title)&&!(/bag|shoe|accessor|jewel/.test(ty)))return 'sets';
    for(var i=0;i<rules.length;i++)if(rules[i][0].test(ty))return rules[i][1];
    return '';
  }
  function tokens(p){return String(p.title||p.t||'').toLowerCase().split(/\W+/).concat(typeof p.tags==='string'?p.tags.toLowerCase().split(/[,|]/):p.tags||String(p.tg||'').split('|'));}
  function score(p,anchor,native){
    var a=tokens(anchor),b=tokens(p),s=native?10:0;
    ['lace','satin','knit','floral','bodycon','mini','midi','maxi','tailored','going-out','work','everyday','vacation','black','white','red','brown','gold','silver'].forEach(function(t){if(a.indexOf(t)>-1&&b.indexOf(t)>-1)s+=8;});
    var ap=Number(anchor.price||anchor.p)||1,pp=Number(p.price||p.p)||1;
    s+=Math.max(0,10-Math.abs(Math.log(pp/ap))*8);
    return s;
  }
  function feed(handle){
    if(!feeds[handle])feeds[handle]=fetch(root()+'/collections/'+handle+'?view=merch&sort_by=best-selling',{credentials:'same-origin'}).then(function(r){if(!r.ok)throw Error('feed');return r.text();}).then(function(t){return JSON.parse(t).items||[];}).catch(function(){return [];});
    return feeds[handle];
  }
  W.discover=function(anchor,intent,limit,surface,exclude){
    var kind=group(anchor),sources=intent==='complementary'?({dresses:['bags','shoes','accessories'],sets:['bags','shoes','accessories'],tops:['bottoms','bags','shoes'],bottoms:['tops','knitwear','shoes'],outerwear:['knitwear','bags','shoes'],knitwear:['bottoms','bags','shoes'],bags:['shoes','dresses','accessories'],shoes:['bags','dresses','matching-sets'],accessories:['bags','dresses','shoes']}[kind]||[]):[kind==='sets'?'matching-sets':kind];
    sources=sources.filter(Boolean);var allowed=sources.map(function(s){return s==='matching-sets'?'sets':s;});
    if(!sources.length)return Promise.resolve([]);
    var id=anchor.id||anchor.product_id;
    var native=id?fetch(root()+'/recommendations/products.json?product_id='+id+'&limit=10&intent='+intent,{credentials:'same-origin'}).then(function(r){return r.ok?r.json():{};}).then(function(j){return j.products||[];}).catch(function(){return [];}):Promise.resolve([]);
    return Promise.all([native,Promise.all(sources.map(feed))]).then(function(parts){
      var all=[],have={};parts[0].forEach(function(p){p._native=true;all.push(p);});parts[1].forEach(function(list){list.forEach(function(it){var p=W.merch.itemToProduct(it,0);all.push(p);});});
      var saved=[];try{saved=JSON.parse(localStorage.getItem('maie_saved_v1')||'[]');}catch(e){}
      var excluded=(exclude||[]).map(String);excluded.push(String(id));
      all=all.filter(function(p){var h=p.handle;if(intent==='related'&&kind==='shoes'&&/boot|sneaker|trainer/i.test(p.title||'')&&!/boot|sneaker|trainer/i.test(anchor.title||''))return false;
        if(!h||have[h]||!p.available||!(p.images||[]).length||excluded.indexOf(String(p.id))>-1||h===anchor.handle||saved.indexOf(h)>-1||allowed.indexOf(group(p))<0)return false;have[h]=1;return true;});
      all.sort(function(a,b){return score(b,anchor,b._native)-score(a,anchor,a._native)||a.handle.localeCompare(b.handle);});
      Object.keys(claimed).forEach(function(h){if(claimed[h]===surface)delete claimed[h];});
      var selected=[],counts={};
      all.forEach(function(p){var g=group(p);if(selected.length>=limit||claimed[p.handle]&&claimed[p.handle]!==surface)return;if(intent==='complementary'&&(counts[g]||0)>=Math.ceil(limit/allowed.length))return;selected.push(p);claimed[p.handle]=surface;counts[g]=(counts[g]||0)+1;});
      return Promise.all(selected.map(function(p){return p._native?p:W.getProduct(p.handle).catch(function(){return null;});}));
    }).then(function(products){
      Object.keys(claimed).forEach(function(h){if(claimed[h]===surface)delete claimed[h];});
      return products.filter(function(p){if(!p||!p.available||claimed[p.handle]&&claimed[p.handle]!==surface)return false;claimed[p.handle]=surface;return true;});
    });
  };
  W.discoverFromSeed=function(handle,intent,limit,surface,exclude){return W.getProduct(handle).then(function(p){return W.discover(p,intent,limit,surface,exclude);});};
})();
